import { useRef, useEffect, MutableRefObject } from "react";
import C from "classnames";
import { Button } from "primereact/button";

import { useSandboxOutput } from "../contexts/SandboxHooks";

import S from "./OutputPanel.module.scss";

interface OutputPanelProps {
  isOpenValue?: boolean;
  updateBadge?: (count: number) => void;
  onBadgeClearRef?: MutableRefObject<(() => void) | null>;
}

const OutputPanel = ({ isOpenValue, updateBadge, onBadgeClearRef }: OutputPanelProps) => {
  const { outputLines, newLinesCount, resetNewLinesCount, clearOutput } = useSandboxOutput();
  const anchorRef = useRef<HTMLDivElement>(null);

  // Register the badge clear callback with the Drawer
  useEffect(() => {
    if (onBadgeClearRef) {
      onBadgeClearRef.current = resetNewLinesCount;
    }
    return () => {
      if (onBadgeClearRef) {
        onBadgeClearRef.current = null;
      }
    };
  }, [onBadgeClearRef, resetNewLinesCount]);

  // Update badge when unseen count changes
  useEffect(() => {
    if (updateBadge) {
      updateBadge(newLinesCount);
    }
  }, [updateBadge, newLinesCount]);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (anchorRef.current) {
      requestAnimationFrame(() => {
        anchorRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    }
  };

  // Auto-scroll to bottom when new output arrives
  useEffect(() => {
    scrollToBottom();
  }, [outputLines]);

  // Auto-scroll when panel is opened
  useEffect(() => {
    if (isOpenValue) {
      scrollToBottom();
    }
  }, [isOpenValue]);

  return (
    <div className={S.outputPanel} tabIndex={0} role="log" aria-label="Sandbox output panel">
      {outputLines.length === 0 ? (
        <div className={S.emptyState}>
          <p>No output yet. Run the application to see terminal output here.</p>
        </div>
      ) : (
        <div className={S.terminal}>
          {outputLines.map(line => (
            <div key={line.id} className={C("atlas-code", S.outputLine)}>
              <span className={S.timestamp}>
                {line.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
              <span className={S.text}>{line.text}</span>
            </div>
          ))}
          <div ref={anchorRef} />
        </div>
      )}
      <Button
        label="Clear"
        className="self-end mx-8"
        size="small"
        severity="secondary"
        onClick={clearOutput}
        disabled={outputLines.length === 0}
      />
    </div>
  );
};

export default OutputPanel;
